import React, { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { useFormik, getIn } from 'formik';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';

import { formatNumberThsndAndDec, formatNumberMagnitude } from '../helpers/stringFormat';
import { getMagnitude } from './valueFunctions';
import { addCurrencyFrequency } from '../helpers/currencyFormat';
import { Typography } from '@mui/material';
import { GreenRedCompactSwitch, TextFieldCustom } from '../helpers/formFields';
import firstCharToUpper, { makePlural } from '../helpers/stringFormat';
import { createSliderMarks, getAverageValue } from './formFunctions';
import { tooltip, generalTooltipText } from '../helpers/tooltip';

const frequencyTransfer = {
    daily: "day",
    monthly: "month",
    quarterly: "quarter",
    yearly: "year",
};

const RevenueValueDriverTTMForm = forwardRef(({ currency, ownProduct, product, editTTMDriverId, editTTMDriver, formActions, onSubmit }, ref) => {
    const [positiveDriver, setPositiveDriver] = useState(true);

    const tooltipText = {
        productLife: "Product life cycle duration refers to the period a product remains active in the market—from its launch through growth, maturity, decline, and until retirement.",
        timeFasterToMarket: `Days ${positiveDriver ? "shortening" : "extension"} of development time when using your product instead of the competitive alternative. E.g. 5 days shorter development time means that the product can be launched earlier and by that bring more profit.`,
    }

    useEffect(() => {
        formik.setFieldValue("annual_revenue", getAverageValue(product.annual_revenue))
        formik.setFieldValue("contribution_margin", getAverageValue(product.contribution_margin))
        formik.setFieldValue("development_cost", getAverageValue(product.development_costs))
        formik.setFieldValue("product_life", product.product_life_cycle_duration.higher)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product]);

    useEffect(() => {
        if (editTTMDriverId) {
            setPositiveDriver(editTTMDriver["positive_driver"])

            formik.setFieldValue("time_faster_to_market", editTTMDriver["time_faster_to_market"])
            formik.setFieldValue("annual_revenue", editTTMDriver["annual_revenue"])
            formik.setFieldValue("contribution_margin", editTTMDriver["contribution_margin"])
            formik.setFieldValue("development_cost", editTTMDriver["development_cost"])
            formik.setFieldValue("product_life", editTTMDriver["product_life"])
            formik.setFieldValue("units_needed", formatNumberThsndAndDec(editTTMDriver["units_needed"]))
            formik.setFieldValue("time_needed", formatNumberThsndAndDec(editTTMDriver["time_needed"]))
            formik.setFieldValue("positive_driver", editTTMDriver["positive_driver"])
            formik.setFieldValue("driver_name", editTTMDriver["driver_name"])
            formik.setFieldValue("comment", editTTMDriver["comment"])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editTTMDriverId])

    useEffect(() => {
        formik.setFieldValue("driver_name", `${positiveDriver ? 'Faster' : 'Slower'} to market`)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positiveDriver])

    const initialValues = {
        id: null,
        revenue_type: 'ttm',
        driver_name: `${positiveDriver ? 'Faster' : 'Slower'} to market`,
        comment: '',
        positive_driver: true,
        type: '',
        currency: currency,
        time_faster_to_market: 0,
        annual_revenue: getAverageValue(product.annual_revenue),
        contribution_margin: getAverageValue(product.contribution_margin),
        development_cost: getAverageValue(product.development_costs),
        product_life: product.product_life_cycle_duration.higher,
        units_needed: 0,
        time_needed: 0,
    }

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) >= 0;
    };

    const validate = (values) => {
        const errors = {};

        if (!values.driver_name) {
            errors.driver_name = 'Driver must have a name';
        }

        if (!isNumeric(values.contribution_margin)) {
            errors.contribution_margin = 'Must be numeric value';
        } else if (!isPositive(values.contribution_margin)) {
            errors.contribution_margin = 'Must be positive or zero';
        } else if (values.contribution_margin > 100) {
            errors.contribution_margin = 'Not more than 100';
        }

        if (!isNumeric(values.product_life)) {
            errors.product_life = 'Must be numeric value';
        } else if (!isPositive(values.product_life)) {
            errors.product_life = 'Must be positive or zero';
        }

        if (!isNumeric(values.time_faster_to_market)) {
            errors.time_faster_to_market = 'Must be numeric value';
        } else if (!isPositive(values.time_faster_to_market)) {
            errors.time_faster_to_market = 'Must be positive or zero';
        }

        if (!isNumeric(values.units_needed)) {
            errors.units_needed = 'Must be numeric value';
        } else if (!isPositive(values.units_needed)) {
            errors.units_needed = 'Must be positive or zero';
        }

        if (!isNumeric(values.time_needed)) {
            errors.time_needed = 'Must be numeric value';
        } else if (!isPositive(values.time_needed)) {
            errors.time_needed = 'Must be positive or zero';
        }

        return errors
    }

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset()
            setPositiveDriver(true)
            formik.setFieldValue("annual_revenue", getAverageValue(product.annual_revenue))
            formik.setFieldValue("contribution_margin", getAverageValue(product.contribution_margin))
            formik.setFieldValue("development_cost", getAverageValue(product.development_costs))
            formik.setFieldValue("product_life", product.product_life_cycle_duration.higher)
        },
    }));

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ ml: 2, maxWidth: 1300 }}>
            <Grid container columnSpacing={2} alignItems="center" rowSpacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            2. Annual profit contribution
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Annual revenue
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Contribution margin{tooltip(generalTooltipText.contributionMargin)}
                            </Typography>
                        </Grid>
                        <Grid item xs={9} />
                        <Grid item xs={3}>
                            <Slider
                                name="annual_revenue"
                                value={formik.values.annual_revenue}
                                onChange={formik.handleChange}
                                valueLabelDisplay="auto"
                                marks={createSliderMarks(product.annual_revenue)}
                                min={product.annual_revenue.lower * 0}
                                max={product.annual_revenue.higher * 1.2}
                                step={getMagnitude(product.annual_revenue.lower) / 10}
                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), product.annual_revenue.currency, null)}
                                sx={{ maxWidth: 300 }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name="contribution_margin"
                                endAdornment="%"
                                value={formik.values.contribution_margin}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("contribution_margin", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "contribution_margin") && Boolean(getIn(formik.errors, "contribution_margin"))}
                                helperText={getIn(formik.touched, "contribution_margin") && getIn(formik.errors, "contribution_margin")}
                            />
                        </Grid>
                        <Grid item xs={7} />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            3. Development costs
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Development costs
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Product life cycle duration{tooltip(tooltipText.productLife)}
                            </Typography>
                        </Grid>
                        <Grid item xs={9} />
                        <Grid item xs={3}>
                            <Slider
                                name="development_cost"
                                value={formik.values.development_cost}
                                onChange={formik.handleChange}
                                valueLabelDisplay="auto"
                                marks={createSliderMarks(product.development_costs)}
                                min={product.development_costs.lower * 0}
                                max={product.development_costs.higher * 1.2}
                                step={getMagnitude(product.development_costs.lower) / 10}
                                valueLabelFormat={value => addCurrencyFrequency(formatNumberMagnitude(value), product.development_costs.currency, null)}
                                sx={{ maxWidth: 300 }}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name="product_life"
                                endAdornment={
                                    <Typography variant="caption">
                                        years
                                    </Typography>
                                }
                                value={formik.values.product_life}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("product_life", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "product_life") && Boolean(getIn(formik.errors, "product_life"))}
                                helperText={getIn(formik.touched, "product_life") && getIn(formik.errors, "product_life")}
                            />
                            <Grid item xs={7} />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {`4. ${positiveDriver ? 'Faster' : 'Slower'} to market`}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Grid container columnGap={2}>
                        <Grid item xs={2} sx={{ mt: 3 }}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                {`Days ${positiveDriver ? 'faster' : 'slower'} to market`}{tooltip(tooltipText.timeFasterToMarket)}
                            </Typography>
                        </Grid>
                        {ownProduct.unit.length > 0 &&
                            <Grid item xs={2}>
                                <Typography variant="caption">
                                    {firstCharToUpper(makePlural(ownProduct.unit))} of {ownProduct.name} needed for development
                                </Typography>
                            </Grid>
                        }
                        {ownProduct.revenue_model === 'subscription' &&
                            <Grid item xs={2}>
                                <Typography variant="caption">
                                    {firstCharToUpper(makePlural(frequencyTransfer[ownProduct.frequency]))} subscription of {ownProduct.name} needed for development
                                </Typography>
                            </Grid>
                        }
                        {ownProduct.unit.length > 0 && ownProduct.revenue_model === 'subscription' ?
                            <Grid item xs={4} />
                            : <Grid item xs={10} />}
                        <Grid item xs={2}>
                            <TextFieldCustom
                                name="time_faster_to_market"
                                endAdornment={
                                    <Typography variant="caption">
                                        days
                                    </Typography>
                                }
                                value={formik.values.time_faster_to_market}
                                onChange={(event) => {
                                    const { value } = event.target
                                    const formattedValue = formatNumberThsndAndDec(value)
                                    formik.setFieldValue("time_faster_to_market", formattedValue || '')
                                }}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "time_faster_to_market") && Boolean(getIn(formik.errors, "time_faster_to_market"))}
                                helperText={getIn(formik.touched, "time_faster_to_market") && getIn(formik.errors, "time_faster_to_market")}
                                greenBackground={positiveDriver}
                                redBackground={!positiveDriver}
                            />
                        </Grid>
                        {ownProduct.unit.length > 0 &&
                            <Grid item xs={2}>
                                <TextFieldCustom
                                    name="units_needed"
                                    endAdornment={
                                        <Typography variant="caption">
                                            {makePlural(ownProduct.unit)}
                                        </Typography>
                                    }
                                    value={formik.values.units_needed}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formik.setFieldValue("units_needed", formattedValue || '')
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={getIn(formik.errors, "units_needed") && Boolean(getIn(formik.errors, "units_needed"))}
                                    helperText={getIn(formik.touched, "units_needed") && getIn(formik.errors, "units_needed")}
                                />
                            </Grid>
                        }
                        {ownProduct.revenue_model === 'subscription' &&
                            <Grid item xs={2}>
                                <TextFieldCustom
                                    name="time_needed"
                                    endAdornment={
                                        <Typography variant="caption">
                                            {makePlural(frequencyTransfer[ownProduct.frequency])}
                                        </Typography>
                                    }
                                    value={formik.values.time_needed}
                                    onChange={(event) => {
                                        const { value } = event.target
                                        const formattedValue = formatNumberThsndAndDec(value)
                                        formik.setFieldValue("time_needed", formattedValue || '')
                                    }}
                                    onBlur={formik.handleBlur}
                                    error={getIn(formik.errors, "time_needed") && Boolean(getIn(formik.errors, "time_needed"))}
                                    helperText={getIn(formik.touched, "time_needed") && getIn(formik.errors, "time_needed")}
                                />
                            </Grid>
                        }
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            5. Driver details
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={2}>
                    <Stack direction="column" gap={1}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                            Value driver{tooltip(generalTooltipText.differentiationValue)}
                        </Typography>
                        <GreenRedCompactSwitch
                            name="positive_driver"
                            leftLabel="Negative"
                            rightLabel="Positive"
                            value={formik.values.positive_driver}
                            onChange={(event) => {
                                formik.handleChange(event)
                                setPositiveDriver(!positiveDriver)
                            }}
                        />

                    </Stack>
                </Grid>
                <Grid item xs={10} />
                <Grid item xs={12}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Name of driver
                            </Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Comment
                            </Typography>
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3}>
                            <TextFieldCustom
                                name="driver_name"
                                value={formik.values.driver_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "driver_name") && Boolean(getIn(formik.errors, "driver_name"))}
                                helperText={getIn(formik.touched, "driver_name") && getIn(formik.errors, "driver_name")}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextFieldCustom
                                name="comment"
                                value={formik.values.comment}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={getIn(formik.errors, "comment") && Boolean(getIn(formik.errors, "comment"))}
                                helperText={getIn(formik.touched, "comment") && getIn(formik.errors, "comment")}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Box sx={{ mt: 3, mb: 4 }}>
                {formActions()}
            </Box>
        </Box>
    )

})

export default RevenueValueDriverTTMForm                